import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import AgentPortalError from 'errors/AgentPortalError';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useForm } from 'react-hook-form';

import SubscriptionDetails from '../../../BookingDetails/Extras/ExtrasSubscriptions/SubscriptionDetails';

import { CANCEL_TRAVEL_PASS_PRODUCT } from 'store/mutations';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { CurrencyInput } from 'components/ui/Inputs';
import { ActionButton, ConfirmButton } from 'components/ui/Button';

import { getRefundMethodsURL } from 'utils/urls';
import { subtract, sum } from 'utils/calculator';

import { CancellationFeeWrapper, CancellationFeeInput, FlexWrapper, StyledNoRevert, StyledInfos } from './Subscription.styles';
import {
  StyledButtonGroup,
  StyledFormFooter,
  StyledInputWrapper,
  StyledErrorMessage
} from '../../../BookingDetails/Extras/ExtrasHotel/ManageHotel/ManageHotel.styles';
import { VAT } from 'constants/subscriptionCancellationReasons';

const ManualCancellation = ({
  itemRef,
  passenger,
  startDate,
  endDate,
  name,
  currency,
  price,
  bookingReference,
  onClose,
  reason,
  membershipNumber,
  travelPassReference,
  bookingWithin14DaysCoolingOffPeriod,
  hasPastBookings
}) => {
  const { push } = useHistory();
  const { register, handleSubmit, formState, setValue } = useForm();

  const { errors } = formState;

  const [preferentialRate, setPreferentialRate] = useState(0);
  const [paymentAmount, changePaymentAmount] = useState(subtract(price, !bookingWithin14DaysCoolingOffPeriod && reason === VAT ? 0 : 1));
  const hasFeeErrors = Boolean(Object.keys(errors).length > 0);

  const [cancelTravelPassProduct, { loading: cancelLoading, error }] = useMutation(CANCEL_TRAVEL_PASS_PRODUCT, {
    onCompleted: ({ cancelTravelPassProduct: { paymentsRefundBalance } }) => {
      if (paymentsRefundBalance < 0) {
        push(getRefundMethodsURL(bookingReference, currency, membershipNumber, false, true));
      }
    },
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const handleOnSubmit = async () => {
    await cancelTravelPassProduct({
      variables: {
        bookingReference,
        itemRef,
        fee: sum([subtract(price, paymentAmount), preferentialRate]),
        cancellationReason: reason,
        travelPassReference
      }
    });
  };

  if (error) {
    throw new AgentPortalError(error);
  }

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)} data-cy="subscription-form" data-testid="subscription-form-manual">
      <SubscriptionDetails
        firstName={passenger?.firstName}
        lastName={passenger?.lastName}
        startDate={moment(startDate).format('YYYY-MM-DD')}
        endDate={moment(endDate).format('YYYY-MM-DD')}
        name={name}
        currency={currency}
        price={price}
        bookingWithin14DaysCoolingOffPeriod={bookingWithin14DaysCoolingOffPeriod}
      />

      <StyledInfos>
        <div>
          <FormattedMessage id="thalyspass.purchase" />:{' '}
          {bookingWithin14DaysCoolingOffPeriod ? (
            <FormattedMessage id="subscription.withinCoolingOff" />
          ) : (
            <FormattedMessage id="subscription.outsideCoolingOff" />
          )}
        </div>
        <div>
          <FormattedMessage id="subscriptions.cancellationReasonTickets" />: {reason}
        </div>
        <div>
          <FormattedMessage id="subscriptions.ticketsTravelled" />:{' '}
          {hasPastBookings ? <FormattedMessage id="interaction.yes" /> : <FormattedMessage id="interaction.no" />}
        </div>
      </StyledInfos>

      <CancellationFeeWrapper>
        <CancellationFeeInput>
          <StyledInputWrapper>
            <strong>
              <FormattedMessage id="bookingSummary.cancellationFees" />
            </strong>

            <FlexWrapper data-testid="manualFee-wrapper">
              {reason === VAT ? (
                <FormattedCurrency currency={currency} amount={subtract(price, paymentAmount)} dataCy="manualFee" />
              ) : (
                <CurrencyInput
                  currency={currency}
                  dataCy="manualFee"
                  dataTestId="manualFee"
                  value={subtract(price, paymentAmount)}
                  min={!bookingWithin14DaysCoolingOffPeriod && reason === VAT ? 0 : 1}
                  max={subtract(price, !bookingWithin14DaysCoolingOffPeriod && reason === VAT ? 0 : 1)}
                  {...register('manualFee', {
                    required: true,
                    onChange: (event) => {
                      const newValue = subtract(price, event.target.value);

                      setValue('manualFee', newValue);
                      changePaymentAmount(newValue);
                    }
                  })}
                  step={0.01}
                />
              )}
            </FlexWrapper>
          </StyledInputWrapper>
        </CancellationFeeInput>

        <CancellationFeeInput>
          <StyledInputWrapper>
            <strong>
              <FormattedMessage id="extras.insurance.modal.total" />
            </strong>
            <FlexWrapper data-testid="currency-price-cancellation-total">
              <FormattedCurrency currency={currency} amount={paymentAmount} dataCy="currency-price-cancellation-fees" />
            </FlexWrapper>
          </StyledInputWrapper>
        </CancellationFeeInput>

        {hasPastBookings && reason === VAT && (
          <>
            <CancellationFeeInput>
              <StyledInputWrapper>
                <strong>
                  <FormattedMessage id="subscriptions.preferentialRates" />:
                </strong>

                <FlexWrapper data-testid="currency-price-preferential-rates-fees">
                  <CurrencyInput
                    currency={currency}
                    dataCy="preferentialRates"
                    dataTestId="preferentialRates"
                    value={preferentialRate}
                    min={0}
                    max={price}
                    {...register('preferentialRates', {
                      required: true,
                      onChange: (event) => {
                        const newValue = event.target.value;

                        setValue('preferentialRates', newValue);
                        setPreferentialRate(newValue);
                      }
                    })}
                    step={0.01}
                  />
                </FlexWrapper>
              </StyledInputWrapper>
            </CancellationFeeInput>

            <CancellationFeeInput>
              <StyledInputWrapper>
                <strong>
                  <FormattedMessage id="extras.refundAmountMessage" />:
                </strong>
                <FlexWrapper data-testid="currency-price-preferential-rates">
                  <FormattedCurrency
                    currency={currency}
                    amount={paymentAmount - preferentialRate}
                    dataCy="currency-price-preferential-rates"
                  />
                </FlexWrapper>
              </StyledInputWrapper>
            </CancellationFeeInput>
          </>
        )}
      </CancellationFeeWrapper>
      <StyledNoRevert>
        <FormattedMessage id="subscription.noRevert" />
      </StyledNoRevert>
      {hasFeeErrors && (
        <StyledErrorMessage data-testid="manual-fees-errors">
          <FormattedMessage id="subscription.manualFeeError" />
        </StyledErrorMessage>
      )}
      <StyledFormFooter>
        <StyledButtonGroup>
          <ConfirmButton
            type="submit"
            data-testid="submit"
            data-cy="submit"
            disabled={cancelLoading}
            loading={cancelLoading}
            variant="primary"
          >
            <FormattedMessage id="extras.hotel.modal.confirm" />
          </ConfirmButton>

          <ActionButton type="button" data-testid="close" onClick={onClose} disabled={cancelLoading}>
            <FormattedMessage id="extras.hotel.modal.close" />
          </ActionButton>
        </StyledButtonGroup>
      </StyledFormFooter>
    </form>
  );
};

ManualCancellation.propTypes = {
  itemRef: PropTypes.string,
  passenger: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string
  }),
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  name: PropTypes.string,
  currency: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bookingReference: PropTypes.string,
  confirmedTimestamp: PropTypes.string,
  onClose: PropTypes.func,
  reason: PropTypes.string,
  bookingWithin14DaysCoolingOffPeriod: PropTypes.bool,
  membershipNumber: PropTypes.string,
  travelPassReference: PropTypes.string,
  hasPastBookings: PropTypes.bool
};

export default ManualCancellation;
